import React from 'react';
import { Brain, Clock } from 'lucide-react';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { gamePredictionButtonPtBR } from '../mappings/gamePredictionButton.pt-br';

const GamePredictionButton = ({
  onClick,
  remainingSpins,
  isBasicUser,
  isMobile,
  resetTime,
  disabled = false
}) => {
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();

  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return gamePredictionButtonPtBR[text] || text;
  };

  // Format number with k notation for values over 99
  const formatSpins = (num) => {
    if (num >= 100) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  // Never fully disable the button - basic users should still be able to spend Star Coins
  // after using their free spin
  const hasSpins = remainingSpins > 0;

  // Format remaining time
  const getFormattedTimeRemaining = () => {
    if (!resetTime) return null;

    try {
      const now = new Date();
      const reset = new Date(resetTime);
      const diffMs = Math.max(0, reset - now);

      // If less than a minute, show "Soon"
      if (diffMs < 60000) return t("Soon");

      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffHrs > 0) {
        return `${diffHrs}h ${diffMins}m`;
      } else {
        return `${diffMins}m`;
      }
    } catch (e) {
      console.error('Error formatting reset time:', e);
      return null;
    }
  };

  const formattedResetTime = resetTime ? getFormattedTimeRemaining() : null;

  if (isMobile) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 relative group
          ${hasSpins
            ? 'bg-white/[0.03] backdrop-blur-sm border border-emerald-500/20 hover:bg-white/[0.05] hover:border-emerald-500/30'
            : 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'}`}
        aria-label={`AI Game Prediction${hasSpins ? ` (${remainingSpins} remaining)` : ''}`}
      >
        <div className="relative">
          <Brain className={`w-3.5 h-3.5 ${hasSpins ? 'text-emerald-400/80' : 'text-white/50 group-hover:text-white/70'}`} />
          {hasSpins && (
            <div className="absolute -top-0.5 -right-0.5 w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse" />
          )}
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 group
        ${hasSpins
          ? 'bg-white/[0.03] backdrop-blur-sm border border-emerald-500/20 hover:bg-white/[0.05] hover:border-emerald-500/30'
          : 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08]'}`}
      aria-label="AI Game Prediction"
      title={!hasSpins ? `Use Star Coins for predictions` : null}
    >
      <Brain className={`w-3.5 h-3.5 ${hasSpins ? 'text-emerald-400/80' : 'text-white/50 group-hover:text-white/70'}`} />
      <span className={`text-xs text-white/70 group-hover:text-white/80 font-mono`}>
        {t('AI Prediction')}
      </span>

      {hasSpins ? (
        <span className="font-mono text-[10px] text-emerald-400/80 group-hover:text-emerald-400/90">
          {formatSpins(remainingSpins)}
        </span>
      ) : (
        !isBasicUser && formattedResetTime && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-white/30" />
            <span className="font-mono text-[10px] text-white/30">
              {formattedResetTime}
            </span>
          </div>
        )
      )}
    </button>
  );
};

export default GamePredictionButton;